import React, { useState, useRef, useEffect } from "react";
import BaseModal from "../../modals/base-modal";
import InlineModal from "../../info-modal/infoModal";
import FormSelect from "../../forms/form-select";
import { instanceMob } from "../../../utils/utils";
import { getBackendError } from "../../../utils/error-helpers";
import useLocationStore from "../config/use-location-store";
import "./styles.css";

const monthLabels = [
    "Enero", "Febrero", "Marzo",
    "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre",
    "Octubre", "Noviembre", "Diciembre",
];

const monthKeyMap = {
    Enero: "january",
    Febrero: "february",
    Marzo: "march",
    Abril: "april",
    Mayo: "may",
    Junio: "june",
    Julio: "july",
    Agosto: "august",
    Septiembre: "september",
    Octubre: "october",
    Noviembre: "november",
    Diciembre: "december",
};

const CreateGoalModal = ({ show, onClose, locationName }) => {
    const [selectedGoalType, setSelectedGoalType] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [monthlyGoals, setMonthlyGoals] = useState({});
    const [savedGoals, setSavedGoals] = useState({});
    const [activeMonths, setActiveMonths] = useState({});
    const [goalId, setGoalId] = useState(null);
    const [infoModal, setInfoModal] = useState(null);
    const [tempValues, setTempValues] = useState({});
    const [goalTypeOptions] = useState([
        { id: 10, value: 10, label: "Ventas general" },
        { id: 20, value: 20, label: "Ventas de planes gimnasio" },
        { id: 30, value: 30, label: "Ventas de productos tienda" },
    ]);


    const [loadingGoalTypes, setLoadingGoalTypes] = useState(false);
    const [loadingGoalData, setLoadingGoalData] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const formRef = useRef(null);
    const { selectedLocation } = useLocationStore();

    const fetchGoalData = () => {
        if (!selectedGoalType || !selectedLocation) return;
        setLoadingGoalData(true);
        instanceMob
            .get(
                `admin/accounting/goal/?goal_type=${selectedGoalType.value}&year=${selectedYear}&location_id=${selectedLocation.id}`
            )
            .then((res) => {
                const goalData = res.data;
                if (goalData?.id) {
                    setGoalId(goalData.id);
                } else {
                    setGoalId(null);
                }

                if (goalData?.months) {
                    const newMonthlyGoals = {};
                    const newActiveMonths = {};
                    Object.entries(goalData.months).forEach(([monthKey, monthData]) => {
                        const label = Object.keys(monthKeyMap).find(k => monthKeyMap[k] === monthKey);
                        if (label) {
                            newMonthlyGoals[label] = monthData.value;
                            newActiveMonths[label] = monthData.active;
                        }
                    });
                    setMonthlyGoals(newMonthlyGoals);
                    setSavedGoals({ ...newMonthlyGoals });
                    setActiveMonths(newActiveMonths);
                } else {
                    const emptyGoals = Object.fromEntries(monthLabels.map((m) => [m, 0]));
                    setMonthlyGoals(emptyGoals);
                    setSavedGoals({ ...emptyGoals });
                    setActiveMonths({});
                }
            })
            .catch((err) => {
                console.error("❌ Error al obtener metas:", err);
                const emptyGoals = Object.fromEntries(monthLabels.map((m) => [m, 0]));
                setMonthlyGoals(emptyGoals);
                setSavedGoals({ ...emptyGoals });
                setActiveMonths({});
                setGoalId(null);
            })
            .finally(() => setLoadingGoalData(false));
    };

    useEffect(() => {
        fetchGoalData();
    }, [selectedGoalType, selectedYear, selectedLocation]);

    const handleValueChange = (month, value) => {
        setTempValues((prev) => ({
            ...prev,
            [month]: value,
        }));

        if (!activeMonths[month]) {
            const previousValue = monthlyGoals[month] || 0;
            if (previousValue === 0 && value != 0 && value !== "") {
                setActiveMonths((prev) => ({
                    ...prev,
                    [month]: true,
                }));
            }
        }
    };

    const handleBlurValue = (month) => {
        const newValue = Number(tempValues[month] || 0);
        const currentValue = savedGoals[month] || 0;

        if (currentValue && newValue !== currentValue) {
            return setInfoModal({
                iconType: "warning",
                title: "Confirmar reemplazo de meta",
                description: (
                    <div className="d-flex flex-column gap-1 text-center">
                        <span className="inline-modal-description">
                            Ya tienes una meta asignada para <strong>{month}</strong>.
                        </span>
                        <span className="inline-modal-description">
                            Valor actual: <strong>{currentValue}</strong>
                        </span>
                        <span className="inline-modal-description">
                            Nuevo valor: <strong>{newValue}</strong>
                        </span>
                        <span className="mt-2 inline-modal-description">¿Deseas reemplazarla?</span>
                    </div>
                ),
                primaryAction: {
                    label: "Sí, reemplazar",
                    onClick: () => {
                        const updatedGoals = {
                            ...monthlyGoals,
                            [month]: newValue,
                        };
                        /*
                        setMonthlyGoals((prev) => ({
                            ...prev,
                            [month]: newValue,
                        }));
                         */
                        // Guardar directamente (Save directly)
                        saveGoals(updatedGoals, activeMonths)
                            .then(() => {
                                fetchGoalData(); // Recargar datos (Reload data)
                                setInfoModal({
                                    iconType: "success",
                                    title: "Meta actualizada",
                                    description: "El cambio se ha guardado correctamente.",
                                    primaryAction: {
                                        label: "Aceptar",
                                        onClick: () => {
                                            setInfoModal(null);
                                        },
                                    },
                                });
                            })
                            .catch((err) => {
                                setInfoModal({
                                    iconType: "error",
                                    title: "Error al actualizar meta",
                                    description: getBackendError(err),
                                    secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
                                });
                            });
                    },
                },
                secondaryAction: {
                    label: "Cancelar",
                    onClick: () => {
                        setTempValues((prev) => ({
                            ...prev,
                            [month]: currentValue,
                        }));
                        setInfoModal(null);
                    },
                },
            });
        }

        setMonthlyGoals((prev) => ({
            ...prev,
            [month]: newValue,
        }));
    };

    const handleCheckboxChange = (month, checked) => {
        setActiveMonths((prev) => ({
            ...prev,
            [month]: checked,
        }));
    };

    const saveGoals = (goals, active) => {
        setIsSaving(true);

        const payload = {
            goal_type: selectedGoalType.id,
            year: selectedYear,
            location_id: selectedLocation?.id,
        };

        monthLabels.forEach((month) => {
            const key = monthKeyMap[month];
            if (active[month]) {
                payload[key] = goals[month] || 0;
                payload[`${key}_active`] = true;
            }
        });

        const request = goalId
            ? instanceMob.patch(`/admin/accounting/goal/${goalId}/update`, payload)
            : instanceMob.post("/admin/accounting/goal/create", payload);

        return request.finally(() => setIsSaving(false));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!selectedGoalType) {
            return setInfoModal({
                iconType: "warning",
                title: "Información incompleta",
                description: "Por favor, selecciona el tipo de meta e ingresa un valor válido.",
                primaryAction: { label: "Completar información", onClick: () => setInfoModal(null) },
                secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
            });
        }

        saveGoals(monthlyGoals, activeMonths)
            .then(() =>
                setInfoModal({
                    iconType: "success",
                    title: "Meta asignada",
                    description: "La meta se guardó correctamente y se reflejará en la gráfica.",
                    primaryAction: {
                        label: "Ver mis metas",
                        onClick: () => {
                            setInfoModal(null);
                            handleCloseModal();
                        },
                    },
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => {
                            setInfoModal(null);
                        },
                    },
                })
            )
            .catch((err) =>
                setInfoModal({
                    iconType: "error",
                    title: "Error al asignar meta",
                    description: getBackendError(err),
                    secondaryAction: { label: "Cerrar", onClick: () => setInfoModal(null) },
                })
            );
    };

    const handleCloseModal = () => {
        const goalTypeToReturn = selectedGoalType;

        setSelectedGoalType(null);
        setSelectedYear(new Date().getFullYear());
        setMonthlyGoals({});
        setActiveMonths({});
        setGoalId(null);
        setInfoModal(null);
        setTempValues({});

        onClose(true, goalTypeToReturn);
    };

    return (
        <>
            <BaseModal
                show={show}
                onClose={() => {
                    handleCloseModal();
                }}
                title="Asigna tu meta"
                subtitle={locationName}
                onSave={() => formRef.current?.requestSubmit()}
                isSaving={isSaving}
                saveText="Guardar Cambios"
                size="xl"
                className="create-goal-modal"
                showCancelButton
            >
                <div className="d-flex flex-column gap-3">
                    <div className="label location text-center">
                        {selectedLocation?.name}
                    </div>
                    <div className="label description text-center">
                        Para asignar tu meta en este modal, primero selecciona el tipo de meta que deseas configurar,
                        ingresa el valor correspondiente en el campo indicado y, una vez completada la información, haz
                        clic en “Guardar cambios”.
                        Esto permitirá que la meta quede registrada y se vea reflejada automáticamente en la gráfica.
                    </div>

                    <div className="d-flex flex-row gap-2">
                        <FormSelect
                            label="Tipo de Meta"
                            name="goal-type"
                            value={selectedGoalType?.value || ""}
                            onChange={(e) => {
                                const selectedOption = goalTypeOptions.find(
                                    (opt) => String(opt.value) === String(e.target.value)
                                );
                                setSelectedGoalType(selectedOption);
                            }}
                            options={goalTypeOptions}
                            placeholder="Selecciona tipo de meta"
                            isLoading={loadingGoalTypes}
                        />
                        <FormSelect
                            label="Año"
                            name="year"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            options={Array.from({ length: 2030 - 2020 + 1 }, (_, i) => {
                                const year = 2020 + i;
                                return { value: year, label: year };
                            }).reverse()}
                            placeholder="Selecciona año"
                        />
                    </div>
                    <form
                        ref={formRef}
                        onSubmit={handleSubmit}
                        className="d-flex flex-column gap-3"
                        style={{ position: "relative" }}
                    >
                        {loadingGoalData && (
                            <div className="loading-overlay">
                                <div className="spinner-border text-secondary" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                            </div>
                        )}
                        <div className="grid-months">
                            <div className="row g-3">
                                {monthLabels.map((month) => (
                                    <div key={month} className="col-12 col-sm-6 col-md-4">
                                        <div
                                            className={`month-card d-flex flex-column gap-2 ${activeMonths[month] ? "active" : ""}`}
                                        >
                                            <div className="w-100 d-flex flex-row align-items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input m-0"
                                                    id={`checkbox-${month}`}
                                                    checked={!!activeMonths[month]}
                                                    onChange={(e) => handleCheckboxChange(month, e.target.checked)}
                                                />
                                                <label
                                                    htmlFor={`checkbox-${month}`}
                                                    className="form-check-label label month mb-0"
                                                >
                                                    {month}
                                                </label>
                                            </div>
                                            <input
                                                type="number"
                                                min="0"
                                                className="input w-100"
                                                value={tempValues?.[month] ?? monthlyGoals?.[month] ?? ""}
                                                onChange={(e) => handleValueChange(month, e.target.value)}
                                                onBlur={() => handleBlurValue(month)}
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </form>
                </div>
            </BaseModal>
            {infoModal && (
                <InlineModal
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default CreateGoalModal;
